import * as THREE from "three";
import { JOINT_LABEL_STYLES } from "../joints/jointStyles";
import { JOINT_LABEL_ACTUATOR_ICON_CLASS, setJointLabelDisplayedName } from "../labelUtils";
import {
  ACTUATED_JOINT_LABEL_ICON_COLOR,
  TENDON_MJCF_REFERENCE_WIDTH,
  TENDON_WRAP_GEOM_HIGHLIGHT_COLOR,
} from "./sceneConstants";

/**
 * Fixed tendon arc colors — must stay visually disjoint from constraint overlays:
 * {@link CONSTRAINT_CONNECT_MARKER_COLOR}, {@link CONSTRAINT_WELD_MARKER_COLOR},
 * {@link CONSTRAINT_JOINT_EQ_MARKER_COLOR}.
 */
export const TENDON_COLORS = [0xe94f67, 0x2563eb, 0x84cc16, 0x0891b2, 0xdc2626];

export function parseMjcfRgba(value) {
  if (typeof value !== "string") return null;
  const parts = value.trim().split(/\s+/).map(Number);
  if (parts.length < 3 || parts.slice(0, 3).some((n) => !Number.isFinite(n))) return null;
  const alpha = Number.isFinite(parts[3]) ? parts[3] : 1;
  return {
    color: new THREE.Color(
      THREE.MathUtils.clamp(parts[0], 0, 1),
      THREE.MathUtils.clamp(parts[1], 0, 1),
      THREE.MathUtils.clamp(parts[2], 0, 1)
    ),
    opacity: THREE.MathUtils.clamp(alpha, 0, 1),
  };
}

export function getTendonVisualizationStyle(
  tendon,
  fallbackColor,
  baseLineWidth,
  defaultOpacity = 0.85,
  useParsedAppearance = true
) {
  if (!useParsedAppearance) {
    return {
      color: fallbackColor,
      opacity: defaultOpacity,
      lineWidth: baseLineWidth,
    };
  }
  const attrs = tendon?.attributes || {};
  const rgba = parseMjcfRgba(attrs.rgba);
  const width = Number(attrs.width);
  const widthScale =
    Number.isFinite(width) && width > 0
      ? THREE.MathUtils.clamp(width / TENDON_MJCF_REFERENCE_WIDTH, 0.5, 5)
      : 1;
  return {
    color: rgba?.color ?? fallbackColor,
    opacity: rgba ? Math.max(rgba.opacity, 0.08) : defaultOpacity,
    lineWidth: baseLineWidth * widthScale,
  };
}

export function setObjectMaterialColor(object, color) {
  const materials = Array.isArray(object?.material) ? object.material : [object?.material];
  materials.forEach((material) => {
    if (!material?.color || typeof material.color.setHex !== "function") return;
    material.color.setHex(color);
    material.needsUpdate = true;
  });
}

export function setTendonWrapGeomMaterialHighlight(material, highlighted) {
  if (!material || !material.color || typeof material.color.setHex !== "function") return;
  const u = material.userData || {};
  material.userData = u;

  if (highlighted) {
    if (!u.tendonWrapBaseAppearance) {
      u.tendonWrapBaseAppearance = {
        color: material.color.getHex(),
        opacity: material.opacity,
        transparent: material.transparent,
        depthWrite: material.depthWrite,
        wireframe: material.wireframe,
      };
    }
    material.color.setHex(TENDON_WRAP_GEOM_HIGHLIGHT_COLOR);
    material.opacity = 1;
    material.transparent = false;
    material.depthWrite = true;
    if ("wireframe" in material) material.wireframe = false;
  } else {
    const base = u.tendonWrapBaseAppearance;
    if (!base) return;
    material.color.setHex(base.color);
    material.opacity = base.opacity;
    material.transparent = base.transparent;
    material.depthWrite = base.depthWrite;
    if ("wireframe" in material) material.wireframe = base.wireframe;
    delete u.tendonWrapBaseAppearance;
  }
  material.needsUpdate = true;
}

export function setTendonWrapGeomObjectHighlight(object, highlighted) {
  const apply = (node) => {
    if (!node?.isMesh || !node.material) return;
    const materials = Array.isArray(node.material) ? node.material : [node.material];
    materials.forEach((material) => setTendonWrapGeomMaterialHighlight(material, highlighted));
  };
  apply(object);
  if (object && typeof object.traverse === "function") {
    object.traverse((child) => {
      if (child !== object) apply(child);
    });
  }
}

export function applyJointActuatorAccent(jointVizGroup, opts) {
  const { showActuators, hasActuator, jointName, actuatorDisplayName } = opts;
  const isActuated = !!(showActuators && hasActuator);
  const labelElement = jointVizGroup.userData.siteLabel?.element;
  if (!labelElement) return;

  const actuatorText =
    typeof actuatorDisplayName === "string" && actuatorDisplayName.trim().length > 0
      ? actuatorDisplayName
      : "";
  const displayText =
    showActuators && hasActuator && actuatorText ? actuatorText : (jointName ?? "");

  labelElement.style.setProperty("color", JOINT_LABEL_STYLES.color, "important");
  labelElement.style.setProperty("background", JOINT_LABEL_STYLES.background, "important");
  labelElement.style.setProperty("border", JOINT_LABEL_STYLES.border, "important");
  labelElement.title = isActuated ? "Actuator assigned" : "";

  setJointLabelDisplayedName(labelElement, displayText);

  const iconWrap = labelElement.querySelector(`.${JOINT_LABEL_ACTUATOR_ICON_CLASS}`);
  if (iconWrap) {
    if (isActuated) {
      iconWrap.style.setProperty("display", "inline-flex", "important");
      iconWrap.style.setProperty("color", ACTUATED_JOINT_LABEL_ICON_COLOR, "important");
    } else {
      iconWrap.style.setProperty("display", "none", "important");
      iconWrap.style.removeProperty("color");
    }
  }
}
